import { BarChart3, Play } from 'lucide-react';
import React from 'react';
import EnhancedProcessSynthesisTab from './EnhancedProcessSynthesisTab';

interface EnhancedProcessSynthesisWrapperProps {
  analysisResults: any;
  isAnalyzing: boolean;
  analysisProgress: any;
  globalComplianceScore: number | null;
  onRefresh: () => void;
}

const EnhancedProcessSynthesisWrapper: React.FC<EnhancedProcessSynthesisWrapperProps> = ({
  analysisResults,
  isAnalyzing,
  analysisProgress,
  globalComplianceScore,
  onRefresh
}) => {
  // Early returns for edge cases - this component handles conditional rendering
  // so the main component always uses the same hooks

  // Check 1: No analysis data at all
  if (!analysisResults) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="max-w-md mx-auto items-center justify-center flex flex-col">
          <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Analysis Data</h3>
          <p className="text-gray-600 mb-4">Please run document analysis first.</p>
          <button
            onClick={onRefresh}
            className="bg-electric-blue text-white px-6 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2"
          >
            <Play className="w-4 h-4" />
            <span>Start Analysis</span>
          </button>
        </div>
      </div>
    );
  }

  // Check 2: Documents uploaded but not analyzed
  const documents = analysisResults?.documents || [];
  const documentAnalyses = analysisResults?.document_analyses || [];

  if (documents.length > 0 && documentAnalyses.length === 0) {
    return (
      <div className="text-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300">
        <div className="max-w-md mx-auto">
          <BarChart3 className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Documents Need Analysis</h3>
          <p className="text-gray-600 mb-4">
            You have {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded, but they haven't been analyzed yet.
          </p>
          <button
            onClick={onRefresh}
            className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <Play className="w-4 h-4" />
            <span>Start Analysis</span>
          </button>
        </div>
      </div>
    );
  }

  // Check 3: Documents analyzed but no synthesis
  const synthesis = analysisResults?.process_synthesis || {};
  if (documentAnalyses.length > 0 && !synthesis) {
    return (
      <div className="text-center py-12 bg-purple-50 rounded-lg border-2 border-dashed border-purple-300">
        <div className="max-w-md mx-auto">
          <BarChart3 className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Process Synthesis Required</h3>
          <p className="text-gray-600 mb-4">
            Your documents have been analyzed. Now let's synthesize them into a process map.
          </p>
          <button
            onClick={onRefresh}
            className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <Play className="w-4 h-4" />
            <span>Generate Process Map</span>
          </button>
        </div>
      </div>
    );
  }

  // All conditions met, render the main component
  return (
    <EnhancedProcessSynthesisTab
      analysisResults={analysisResults}
      isAnalyzing={isAnalyzing}
      analysisProgress={analysisProgress}
      globalComplianceScore={globalComplianceScore}
      onRefresh={onRefresh}
    />
  );
};

export default EnhancedProcessSynthesisWrapper;